import { useState, useEffect, useRef } from 'react';
import { useAutoCapitalize } from '../../hooks/useAutoCapitalize';
import { useNavigate, Navigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { 
  Box, Button, Container, Flex, Text, VStack, Avatar, useToast, 
  IconButton, Heading, Alert, AlertIcon, Link, Spinner, Center,
  Textarea
} from '@chakra-ui/react';
import { ArrowForwardIcon, ChatIcon, RepeatIcon } from '@chakra-ui/icons';
import { useActivityResponse } from '../../hooks/useActivityResponse';
import ErrorBoundary from '../../components/ErrorBoundary';
import { useCourse } from '../../contexts/CourseContext';
import { useCourseConfig } from '../../hooks/useCourseConfig';
import { useConsentStatus } from '../../hooks/useConsentStatus';
import { usePreferencesStatus } from '../../hooks/usePreferencesStatus';
import MarkdownRenderer from '../../components/MarkdownRenderer';
import { DEFAULT_COURSE_CODE } from '../../constants/config';

// Constants
const LLM_MODEL = 'openai';

interface MessageAsset {
  id: string;            // Asset ID from storage
  type: 'image' | 'video' | 'document';
  url?: string;          // Will be populated client-side
  alt?: string;          // Alt text for accessibility
}

interface Message {
  id: string;
  content: string;        // Markdown content
  isUser: boolean;
  timestamp: string;
  assets?: MessageAsset[]; // For future asset support
  metadata?: {
    [key: string]: any;    // For future extensions
  };
}

// Custom error fallback for chat
const ChatErrorFallback = ({ error, resetErrorBoundary }: { error: Error | null, resetErrorBoundary: () => void }) => {
  const navigate = useNavigate();

  const handleReset = () => {
    resetErrorBoundary();
    window.location.reload();
  };

  return (
    <Container maxW="container.md" py={8}>
      <Alert status="error" borderRadius="md" mb={6}>
        <AlertIcon />
        <Box flex="1">
          <Heading size="md" mb={2}>Chat Error</Heading>
          <Text>We're having trouble loading the chat. Please try refreshing the page.</Text>
          {process.env.NODE_ENV === 'development' && error && (
            <Box mt={2} p={2} bg="blackAlpha.100" borderRadius="md" fontSize="sm">
              <Text fontWeight="bold">Error details:</Text>
              <Text as="pre" whiteSpace="pre-wrap" fontFamily="mono">
                {error.message}
              </Text>
            </Box>
          )}
        </Box>
      </Alert>
      
      <VStack spacing={4}>
        <Button
          leftIcon={<RepeatIcon />}
          colorScheme="blue"
          onClick={handleReset}
        >
          Reload Chat
        </Button>
        <Button
          variant="outline"
          onClick={() => navigate('/')}
        >
          Return to Course
        </Button>
      </VStack>
    </Container>
  );
};

// Main ChatPage component
// Helper functions for contextual success messages
const getSuccessTitle = (chatConfig?: any, prompt?: string) => {
  // Use course-specific title if configured
  if (chatConfig?.successMessages?.title) {
    return chatConfig.successMessages.title;
  }
  // Fall back to keyword detection
  if (!prompt) return "Submitted!";
  if (prompt.includes('presentation')) return "You're all done!";
  if (prompt.includes('goal') || prompt.includes('workplace')) return "Goal Submitted!";
  if (prompt.includes('reflection') || prompt.includes('reflect')) return "Reflection Complete!";
  return "Submitted!";
};

const getSuccessMessage = (chatConfig?: any, prompt?: string) => {
  // Use course-specific message if configured
  if (chatConfig?.successMessages?.message) {
    return chatConfig.successMessages.message;
  }
  // Fall back to keyword detection
  if (!prompt) return "Thanks for your submission.";
  if (prompt.includes('presentation')) return "Thanks for reflecting on your presentation outline.";
  if (prompt.includes('goal') || prompt.includes('workplace')) return "Thanks for submitting, your Rad Learning Educator is reviewing the goal and will forward it to you soon.";
  if (prompt.includes('reflection') || prompt.includes('reflect')) return "Thanks for your reflection. Your insights have been saved.";
  return "Thanks for your submission.";
};

function ChatPageContent() {
  const [messages, setMessages] = useState<Message[]>([]);
  const { 
    value: input, 
    onChange: handleInputChange, 
    onKeyDown: handleKeyDown, 
    ref: textareaRef, 
    setValue: setInput 
  } = useAutoCapitalize('') as {
    value: string;
    onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
    onKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
    ref: React.RefObject<HTMLTextAreaElement>;
    setValue: (value: string) => void;
  };

  // Auto-resize textarea
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      // Reset height to get the correct scrollHeight
      textarea.style.height = 'auto';
      // Set the height to scrollHeight to fit content, with max height of 200px
      textarea.style.height = `${Math.min(textarea.scrollHeight, 200)}px`;
    }
  }, [input, textareaRef]);
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const messageRefs = useRef<{[key: string]: HTMLDivElement | null}>({});
  const navigate = useNavigate();
  const toast = useToast();
  const [userMessageCount, setUserMessageCount] = useState(0);
  const [showPopup, setShowPopup] = useState(false);
  const [showExitConfirmation, setShowExitConfirmation] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [exitResponse, setExitResponse] = useState('');
  const [isSavingExitResponse, setIsSavingExitResponse] = useState(false);
  const [hasCompletedRequiredTurns, setHasCompletedRequiredTurns] = useState(false);
  const hasAutoStartedRef = useRef(false);
  const [isInitializingChat, setIsInitializingChat] = useState(false);
  const { chat } = useCourseConfig();
  const exitAfter = chat?.exitAfter ?? 5;
  const requireExitResponse = chat?.requireExitResponse ?? false;
  
  const { loading: loadingConsent } = useConsentStatus();
  const { loading: loadingPrefs } = usePreferencesStatus();

  // Handle scroll behavior when messages change
  useEffect(() => {
    if (messages.length === 0) return;
    
    const lastMessage = messages[messages.length - 1];
    
    if (lastMessage.isUser) {
      // For user messages, scroll to bottom as before
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    } else {
      // For LLM messages, scroll to the top of the message
      const messageElement = messageRefs.current[lastMessage.id];
      if (messageElement) {
        messageElement.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    }
  }, [messages]);

  const { currentCourse, isLoading: isLoadingCourse, error: courseError } = useCourse();

  // Hook for exit activity response - use course-specific activity key
  const exitActivityKey = currentCourse?.code === 'RMIT2A' ? 'post_class_RMIT2A' : 'exit_goal_nle';
  const { saveResponse: saveExitResponse, activity: exitActivity } = useActivityResponse(exitActivityKey);

  // Check if user has already completed required turns (for returning users)
  useEffect(() => {
    if (userMessageCount >= exitAfter && exitAfter > 0) {
      setHasCompletedRequiredTurns(true);
    }
  }, [userMessageCount, exitAfter]);

  const addMessageToChat = (role: 'user' | 'assistant', content: string) => {
    const newMessage: Message = {
      id: Date.now().toString(),
      content,
      isUser: role === 'user',
      timestamp: new Date().toISOString(),
    };
    
    setMessages(prev => [...prev, newMessage]);
    
    if (role === 'user') {
      setUserMessageCount(prev => prev + 1);
    }
  };

  // Auto-start with initial LLM message if configured
  useEffect(() => {
    // Only auto-start if this course has the flag configured, chat is empty, not loading, and hasn't already started
    if (chat?.autoStartWithActivity && messages.length === 0 && !isLoading && !hasAutoStartedRef.current) {
      console.log('[Chat] Auto-starting with initial LLM message for activity:', chat.autoStartWithActivity);
      
      const generateInitialMessage = async () => {
        hasAutoStartedRef.current = true; // Mark as started to prevent duplicates
        setIsInitializingChat(true); // Show initializing state
        setIsLoading(true);
        try {
          // Call LLM with a special message to trigger initial response
          const { data, error } = await supabase.functions.invoke('llm-chat', {
            body: {
              message: 'START_CONVERSATION', // Special message to trigger initial response
              model: LLM_MODEL,
              courseId: currentCourse?.id,
              courseCode: currentCourse?.code || DEFAULT_COURSE_CODE,
              turnCount: 1, // Use turn 1 for initial message (database constraint requires positive integers)
              includeActivityResponses: chat?.includeActivityResponses,
              autoStartWithActivity: chat.autoStartWithActivity, // Pass the activity key
            },
          });

          if (error) throw error;
          
          // Add AI response as first message
          if (data?.response) {
            addMessageToChat('assistant', data.response);
            console.log('[Chat] Initial message added successfully');
          }
        } catch (error) {
          console.error('[Chat] Error generating initial message:', error);
          toast({
            title: 'Error',
            description: 'Failed to start conversation. Please try again.',
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
        } finally {
          setIsLoading(false);
          setIsInitializingChat(false); // Hide initializing state
        }
      };

      generateInitialMessage();
    }
  }, [chat?.autoStartWithActivity, messages.length, isLoading, currentCourse, chat?.includeActivityResponses, toast]);

  const handleContinueChat = () => {
    setShowPopup(false);
  };

  const handleExitChat = () => {
    setShowPopup(false);
    setShowExitConfirmation(true);
  };

  const handleReturnToCourse = () => {
    navigate('/s/landing');
  };

  const handleExitWithResponse = async () => {
    if (!exitResponse.trim()) {
      toast({
        title: 'Goal Required',
        description: 'Please set your workplace goal before exiting.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsSavingExitResponse(true);
    try {
      await saveExitResponse(exitResponse);
      setShowExitConfirmation(false);
      setShowSuccessPopup(true);
    } catch (error) {
      console.error('Error saving exit response:', error);
      toast({
        title: 'Error',
        description: 'Failed to save your goal. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSavingExitResponse(false);
    }
  };

  // Show loading state while course is being resolved
  if (isLoadingCourse || loadingConsent || loadingPrefs) {
    return (
      <Center height="100vh">
        <VStack spacing={4}>
          <Spinner size="xl" />
          <Text>Loading your course...</Text>
        </VStack>
      </Center>
    );
  }

  // Show error state if course couldn't be loaded
  if (courseError) {
    return (
      <Center height="100vh">
        <VStack spacing={4} maxW="md" textAlign="center">
          <Alert status="error" borderRadius="md">
            <AlertIcon />
            <Box>
              <Text fontWeight="bold">Error loading course</Text>
              <Text fontSize="sm">{courseError.message}</Text>
            </Box>
          </Alert>
          <Button 
            colorScheme="purple" 
            onClick={() => window.location.reload()}
            leftIcon={<RepeatIcon />}
          >
            Retry
          </Button>
        </VStack>
      </Center>
    );
  }

  // If no course is assigned, do not allow direct access to /chat
  if (!currentCourse) {
    return <Navigate to="/" replace />;
  }

  // Show initializing state while auto-start is loading
  if (isInitializingChat && messages.length === 0) {
    return (
      <Container maxW="4xl" py={8}>
        <VStack spacing={6} align="center" height="60vh" justify="center">
          <Spinner size="xl" color="purple.500" />
          <Text fontSize="lg" color="gray.600" textAlign="center">
            Initializing your personalized conversation...
          </Text>
          <Text fontSize="sm" color="gray.500" textAlign="center">
            I'm preparing a response based on your earlier answers. This will just take a moment.
          </Text>
        </VStack>
      </Container>
    );
  }

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!input.trim() || isLoading || isInitializingChat || !currentCourse) return;
    
    const userMessage = input;
    setInput('');
    setIsLoading(true);
    
    try {
      // Calculate turn count BEFORE updating state
      const absoluteTurnCount = userMessageCount + 1;
      
      // Calculate cyclic turn (1-5 repeating pattern)
      // Turn 1, 2, 3, 4, 5, then back to 1, 2, 3, 4, 5...
      const cyclicTurn = ((absoluteTurnCount - 1) % exitAfter) + 1;
      
      // Add user message to UI (this will increment userMessageCount asynchronously)
      addMessageToChat('user', userMessage);
      
      // Debug logging
      console.log(`[Chat] Message #${absoluteTurnCount}, Cyclic Turn: ${cyclicTurn} (exitAfter: ${exitAfter})`);
      
      // Call Supabase function with course from context
      const { data, error } = await supabase.functions.invoke('llm-chat', {
        body: {
          message: userMessage,
          model: LLM_MODEL,
          courseId: currentCourse?.id, // UUID preferred
          courseCode: currentCourse?.code || DEFAULT_COURSE_CODE, // fallback for safety
          turnCount: cyclicTurn, // Cyclic turn number (1-5) for prompt selection
          includeActivityResponses: chat?.includeActivityResponses, // Activity keys to include in LLM prompt
        },
      });

      if (error) throw error;
      
      // Add AI response to UI
      if (data?.response) {
        addMessageToChat('assistant', data.response);
      }
      
      // Show popup after every exitAfter messages (when cyclic turn resets to 1)
      // This happens when absoluteTurnCount is a multiple of exitAfter
      if (exitAfter > 0 && absoluteTurnCount % exitAfter === 0) {
        setShowPopup(true);
        setHasCompletedRequiredTurns(true); // Mark that required turns are completed
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast({
        title: 'Error',
        description: 'Failed to send message. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box bg="white" minH="100vh" py={12}>
      <Container maxW="4xl">
        <VStack spacing={3} textAlign="center" mb={6}>
          <Heading as="h1" size="2xl" color="gray.800">
            Rad Learning Tutor
          </Heading>
        </VStack>

        <Box 
          bg="white" 
          p={4} 
          borderRadius="xl" 
          borderWidth="2px"
          borderColor="#C200FB"
          boxShadow="sm"
        >
          {!showExitConfirmation && (
            <Box 
              bg="#F9E6FF" 
              p={3} 
              mb={4} 
              borderRadius="md"
              borderLeft="4px solid"
              borderColor="#C200FB"
            >
              <Text 
                color="gray.800" 
                fontSize="sm"
                textAlign="center"
                lineHeight="tall"
              >
                You'll need to answer the Tutor's questions five times. After that you'll be able to use the exit button to set your goal and finish.
              </Text>
            </Box>
          )}

          <VStack 
            ref={messagesContainerRef}
            spacing={3} 
            align="stretch" 
            minH="40vh" 
            maxH="55vh" 
            overflowY="auto" 
            mb={2}
          >
            {showExitConfirmation ? (
              <VStack h="100%" justify="center" spacing={6} px={2} textAlign="center">
                {requireExitResponse ? (
                  <>
                    <Heading as="h2" size="lg" color="#C200FB">
                      One last step!
                    </Heading>
                    
                    <Box w="100%" maxW="md">
                      <Text fontSize="md" fontWeight="medium" color="gray.700" mb={2} textAlign="left">
                        {exitActivity?.prompt || 'Please share your thoughts...'}
                      </Text>
                      <Textarea
                        value={exitResponse}
                        onChange={(e) => setExitResponse(e.target.value)}
                        placeholder={exitActivity?.config?.placeholder || 'Share your thoughts...'}
                        size="md"
                        resize="vertical"
                        minHeight="80px"
                        maxHeight="150px"
                        bg="white"
                        borderColor="gray.300"
                        _focus={{
                          borderColor: '#C200FB',
                          boxShadow: '0 0 0 1px #C200FB'
                        }}
                      />
                    </Box>

                    <VStack spacing={3} w="100%" maxW="md">
                      <Button
                        onClick={handleExitWithResponse}
                        bg="#C200FB"
                        color="white"
                        _hover={{ bg: '#A500D9' }}
                        _active={{ bg: '#8B00B8' }}
                        size="lg"
                        w="100%"
                        isLoading={isSavingExitResponse}
                        loadingText="Saving..."
                        disabled={!exitResponse.trim()}
                      >
                        Set Goal & Exit
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => setShowExitConfirmation(false)}
                        size="md"
                        w="100%"
                      >
                        Cancel
                      </Button>
                    </VStack>
                  </>
                ) : (
                  <>
                    <Heading as="h2" size="lg" color="#C200FB">
                      Thank You for Learning with Us!
                    </Heading>
                    <Text fontSize="lg" color="gray.700" mb={4}>
                      You've completed your learning session with the Rad Learning Tutor.
                    </Text>
                    <VStack spacing={3} align="center">
                      <Text fontSize="lg" fontWeight="bold" color="gray.700">
                        We'll see you at the workshop soon!
                      </Text>
                      <Text fontSize="md" color="gray.600">
                        You can log back in to review this material at any time before the workshop if you'd like.
                      </Text>
                    </VStack>
                    <Button
                      onClick={handleReturnToCourse}
                      bg="#C200FB"
                      color="white"
                      _hover={{ bg: '#A500D9' }}
                      _active={{ bg: '#8B00B8' }}
                      size="lg"
                      mt={4}
                    >
                      Return to Course Page
                    </Button>
                  </>
                )}
              </VStack>
            ) : messages.length === 0 ? (
              <VStack h="100%" justify="center" spacing={3} px={2} textAlign="center">
                <VStack spacing={2} maxW="xl" mx="auto">
                  <Text fontSize="lg" fontWeight="medium" color="gray.700">
                    Now that you've given consent and entered your preferences, you're ready to talk to the tutor.
                  </Text>
                  <Text color="gray.600" fontStyle="italic" mt={2}>
                    You can begin by typing "Hello"
                  </Text>
                </VStack>
              </VStack>
            ) : (
              <VStack spacing={3} align="stretch">
                {messages.map((message) => (
                  <Flex 
                    key={message.id} 
                    ref={el => messageRefs.current[message.id] = el}
                    justify={message.isUser ? 'flex-end' : 'flex-start'}
                  >
                    {!message.isUser && (
                      <Avatar 
                        bg="#C200FB" 
                        icon={<ChatIcon color="white" />} 
                        size="sm" 
                        mr={2} 
                      />
                    )}
                    <Box
                      maxW="80%"
                      p={3}
                      borderRadius="lg"
                      bg={message.isUser ? 'yellow.200' : 'gray.50'}
                      color={message.isUser ? 'black' : 'gray.800'}
                      borderWidth={message.isUser ? '1px' : '1px'}
                      borderColor={message.isUser ? 'gray.200' : 'gray.100'}
                      boxShadow={message.isUser ? 'sm' : 'none'}
                      overflowX="auto"
                    >
                      <MarkdownRenderer 
                        content={message.content} 
                        isUser={message.isUser} 
                      />
                    </Box>
                  </Flex>
                ))}
                <div ref={messagesEndRef} />
              </VStack>
            )}
          </VStack>

          {!showExitConfirmation && (
            <Box mt={2} position="relative">
              {showPopup ? (
              <Box 
                bg="white" 
                p={6} 
                borderRadius="lg" 
                borderWidth="2px"
                borderColor="#C200FB"
                textAlign="center"
              >
                <Text fontSize="lg" mb={6}>
                  Nice work! You've finished the 5 required turns.<br />
                  Want to keep chatting? Go for it. Ready to wrap up? Exit to complete a reflection.
                </Text>
                <Flex justify="center" gap={4}>
                  <Button
                    onClick={handleContinueChat}
                    bg="#C200FB"
                    color="white"
                    _hover={{ bg: '#A500D9' }}
                    _active={{ bg: '#8B00B8' }}
                    flex={1}
                  >
                    Continue
                  </Button>
                  <Button
                    onClick={handleExitChat}
                    bg="#22C55E"
                    color="white"
                    _hover={{ bg: '#16A34A' }}
                    _active={{ bg: '#15803D' }}
                    flex={1}
                  >
                    Exit
                  </Button>
                </Flex>
              </Box>
            ) : (
              <form onSubmit={handleSendMessage}>
              <Box 
                bg="yellow.50" 
                p={3} 
                borderRadius="md"
                border="1px solid"
                borderColor="yellow.200"
              >
                {isInitializingChat && (
                  <Flex mb={3} align="center" color="purple.600">
                    <Spinner size="sm" mr={2} />
                    <Text fontSize="sm">Preparing your personalized response...</Text>
                  </Flex>
                )}
                <Flex>
                  <Box position="relative" flex={1} mr={2}>
                    <Box
                      as="textarea"
                      ref={textareaRef}
                      value={input}
                      onChange={handleInputChange}
                      onKeyDown={handleKeyDown}
                      placeholder={isInitializingChat ? "Please wait while I prepare your response..." : "Type your message here"}
                      disabled={isLoading || isInitializingChat}
                      bg="white"
                      borderColor="gray.300"
                      borderRadius="md"
                      p={3}
                      width="100%"
                      minH="44px"
                      maxH="200px"
                      rows={1}
                      autoCapitalize="sentences"
                      autoCorrect="on"
                      spellCheck="true"
                      autoComplete="off"
                      autoFocus
                      resize="none"
                      overflowY="auto"
                      lineHeight="tall"
                      enterKeyHint="send"
                      _hover={{ borderColor: 'gray.400' }}
                      _focus={{
                        bg: 'white',
                        borderColor: '#C200FB',
                        boxShadow: '0 0 0 1px #C200FB',
                        outline: 'none',
                      }}
                      sx={{
                        '&': {
                          minHeight: '44px',
                          maxHeight: '200px',
                          height: 'auto',
                          paddingTop: '0.75rem',
                          paddingBottom: '0.75rem',
                          lineHeight: '1.5',
                          transition: 'none',
                        },
                        '&::-webkit-scrollbar': {
                          width: '4px',
                        },
                        '&::-webkit-scrollbar-track': {
                          background: 'transparent',
                        },
                        '&::-webkit-scrollbar-thumb': {
                          background: '#C200FB',
                          borderRadius: '4px',
                        },
                      }}
                    />
                  </Box>
                  
                  {/* Send button */}
                  <IconButton
                    type="submit"
                    colorScheme="purple"
                    aria-label="Send message"
                    icon={<ArrowForwardIcon />}
                    isDisabled={!input.trim() || isLoading || isInitializingChat}
                    isRound
                    size="md"
                  />
                </Flex>
                
                {/* Exit button and warning on same line */}
                <Flex 
                  direction={{ base: 'column', md: 'row' }}
                  justify={{ base: 'flex-start', md: 'space-between' }} 
                  align={{ base: 'flex-start', md: 'flex-start' }} 
                  mt={3}
                >
                  {/* On mobile: Exit button first, on desktop: Warning first */}
                  <VStack 
                    spacing={2} 
                    align={{ base: 'flex-start', md: 'flex-end' }}
                    order={{ base: 1, md: 2 }}
                    mb={{ base: 4, md: 0 }}
                  >
                    <Button
                      onClick={handleExitChat}
                      bg={hasCompletedRequiredTurns ? "#22C55E" : "gray.300"}
                      color={hasCompletedRequiredTurns ? "white" : "gray.500"}
                      _hover={{ 
                        bg: hasCompletedRequiredTurns ? "#16A34A" : "gray.300",
                        cursor: hasCompletedRequiredTurns ? "pointer" : "not-allowed"
                      }}
                      _active={{ 
                        bg: hasCompletedRequiredTurns ? "#15803D" : "gray.300"
                      }}
                      size="sm"
                      px={6}
                      disabled={!hasCompletedRequiredTurns}
                      title={!hasCompletedRequiredTurns ? "Not yet available" : "Set your goal and finish"}
                    >
                      Exit
                    </Button>
                    
                    {/* Progressive disclosure text - visually connected to button */}
                    {hasCompletedRequiredTurns && !showPopup && (
                      <Box 
                        p={2} 
                        bg="green.50" 
                        borderRadius="md" 
                        border="1px solid" 
                        borderColor="green.200"
                        textAlign={{ base: 'left', md: 'right' }}
                        minWidth="200px"
                      >
                        <Text fontSize="sm" color="green.700">
                          Finished exploring?<br />
                          This is how you complete the activity.
                        </Text>
                      </Box>
                    )}
                  </VStack>
                  
                  {/* Generative AI warning - on mobile: last, on desktop: first */}
                  <Box 
                    p={3} 
                    bg="white" 
                    borderRadius="md" 
                    borderWidth="1px"
                    borderColor="#F59E0B"
                    borderStyle="solid"
                    opacity={0.8}
                    maxWidth={{ base: '100%', md: '400px' }}
                    order={{ base: 3, md: 1 }}
                    mr={{ base: 0, md: 8 }}
                  >
                    <Text fontSize="sm" color="gray.500">
                      Generative AI can make mistakes.<br />
                      <Link 
                        href="https://www.radlearning.com.au/contact" 
                        color="#C200FB"
                        fontWeight="medium"
                        isExternal
                        _hover={{ textDecoration: 'underline' }}
                      >
                        Ask your Rad Learning Educator for help
                      </Link> if you're ever concerned about what you learn here.
                    </Text>
                  </Box>
                </Flex>
              </Box>
            </form>
            )}
          </Box>
          )}
        </Box>

        {/* Success Popup */}
        {showSuccessPopup && (
          <Box
            position="fixed"
            top="0"
            left="0"
            right="0"
            bottom="0"
            bg="rgba(0, 0, 0, 0.5)"
            display="flex"
            alignItems="center"
            justifyContent="center"
            zIndex={1000}
          >
            <Box
              bg="white"
              p={8}
              borderRadius="lg"
              borderWidth="2px"
              borderColor="#C200FB"
              textAlign="center"
              maxW="md"
              mx={4}
            >
              <VStack spacing={4}>
                <Text fontSize="2xl" fontWeight="bold" color="#C200FB">
                  ✅ {getSuccessTitle(chat, exitActivity?.prompt)}
                </Text>
                <Text fontSize="lg" color="gray.700">
                  {getSuccessMessage(chat, exitActivity?.prompt)}
                </Text>
                <Button
                  onClick={() => {
                    setShowSuccessPopup(false);
                    navigate('/s/landing');
                  }}
                  bg="#C200FB"
                  color="white"
                  _hover={{ bg: '#A500D9' }}
                  size="lg"
                  w="100%"
                >
                  Close
                </Button>
              </VStack>
            </Box>
          </Box>
        )}
      </Container>
    </Box>
  );
}

// Export the wrapped component
export function ChatPage() {
  return (
    <ErrorBoundary 
      fallback={<ChatErrorFallback error={null} resetErrorBoundary={() => {}} />}
      onError={(error: Error, errorInfo: React.ErrorInfo) => {
        console.error('ChatPage error boundary caught an error:', error, errorInfo);
        // You could also log this to an error tracking service
      }}
    >
      <ChatPageContent />
    </ErrorBoundary>
  );
}
